// GlobalChipSelector.tsx
import React from 'react';

export interface ChipOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface GlobalChipSelectorProps {
  label?: string;
  options: ChipOption[];
  value: string | null;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  icon?: string;
  allowDeselect?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
}

export const GlobalChipSelector: React.FC<GlobalChipSelectorProps> = ({
  label,
  options,
  value,
  onChange,
  required = false,
  disabled = false,
  loading = false,
  error = '',
  icon,
  allowDeselect = false,
  emptyMessage = 'No options available',
  loadingMessage = 'Loading...',
}) => {
  const handleClick = (optionValue: string, optionDisabled?: boolean) => {
    if (disabled || loading || optionDisabled) return;
    if (allowDeselect && value === optionValue) {
      onChange('');
    } else {
      onChange(optionValue);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {label && (
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#ef4444' : '#1e3a8a',
          marginBottom: '10px',
        }}>
          {icon && <i className={icon} style={{ fontSize: '13px' }} />}
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {loading ? (
          <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '13px', fontWeight: 500 }}>
            {loadingMessage}
          </div>
        ) : options.length === 0 ? (
          <div style={{ padding: '6px 14px', color: '#64748b', fontSize: '13px', fontWeight: 500 }}>
            {emptyMessage}
          </div>
        ) : (
          options.map((option) => {
            const isSelected = value === option.value;
            const isDisabled = disabled || option.disabled;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleClick(option.value, option.disabled)}
                disabled={isDisabled}
                className="global-chip-button"
                style={{
                  padding: '8px 16px',
                  border: isSelected ? '1.5px solid #3b82f6' : '1.5px solid #e5e7eb',
                  borderRadius: '8px',
                  background: isSelected
                    ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'
                    : 'white',
                  color: isSelected ? 'white' : '#1f2937',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  outline: 'none',
                  boxShadow: isSelected ? '0 2px 8px rgba(59, 130, 246, 0.25)' : 'none',
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                {option.label}
              </button>
            );
          })
        )}
      </div>

      {error && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '12px',
          color: '#ef4444',
          fontWeight: 500,
        }}>
          {error}
        </span>
      )}

      <style>{`
        .global-chip-button {
          transition: all 0.2s ease;
        }
        .global-chip-button:hover:not(:disabled) {
          transform: translateY(-1px);
        }
        .global-chip-button:active:not(:disabled) {
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
};

export default GlobalChipSelector;